import numeral from 'numeral';
import {decimalFormat, financeFormat, ratioValueFormat, thousandthFormat} from '../common';
import {MIN_VALUE_DEFAULTS, MIN_VALUE_LIMITS, PRECISION_LIMITS, PRECISION_MAP} from './contant';
import {ICommonValueFormatParams, IFormatSmallPercent, IFormatSmallValue} from './_type';

// todo 空值和非数值判断已在上层dataFormat中处理，这里的方法是否还需要添加
/**
 * @description: arpu极小值类型指标处理
 * @param {IFormatSmallValue} params 入参
 * @return {string}
 */
export const formatSmallValue = (params: IFormatSmallValue) => {
  const {value, type, decimal, emptyValue} = params;
  const val = value - 0;
  // 当前不存在小于0的情况，这里做个兜底
  if (val <= 0) {
    // return parseFloat(val.toFixed(2));
    // 用numeral转换，可以使0的时候也保留0.00
    return numeral(value).format('0.00');
  }
  // 处理大于0且小于1的情况
  if (val > 0 && val < 1) {
    // 查找对应的精度
    const index = PRECISION_LIMITS.findIndex((limit) => val <= limit);
    if (index !== -1) {
      return parseFloat(val.toFixed(PRECISION_MAP[index]));
    }
  }
  return commonValueFormat({value, type, decimal, fillZero: false, emptyValue});
};

/**
 * @description: 极小值处理相关，计算极小值时，需要保留的位数
 * @param {number} value  值
 * @param {number} decimal 小数位
 * @param {boolean} isAxis 是否是坐标轴
 * @return {string}
 */
const getFixedNumber = (value: number, decimal: number, isAxis = false) => {
  // 坐标轴：value * 100后获得的百分比为0则保留0，如果百分比大于0%小于0.1%（实际坐标轴为0.0X%）则保留2位小数，以此类推
  // 浮窗等数据展示：除了0之外，在坐标轴基础上+1位
  const num = isAxis ? 0 : 1;
  if (value === 0 && isAxis) {
    return 0;
  }
  // 极小值处理，返回对应精度
  const index = MIN_VALUE_LIMITS.findIndex((limit) => value <= limit);
  if (index !== -1) {
    return MIN_VALUE_DEFAULTS[index] + num;
  }
  // 对于未在查找表中明确处理的值，直接返回decimal
  return decimal;
};

/**
 * @description: 百分比极小值处理
 * @param {IFormatSmallPercent} params 入参
 * @return {string}
 */
export const formatSmallPercent = (params: IFormatSmallPercent) => {
  const {value, decimal,isAxis, emptyValue} = params;
  // 取绝对值，因为会传进来负数
  const val = Math.abs(value);
  let fixedNumber = getFixedNumber(val, decimal, isAxis);
  if (Math.abs(value) === 0) {
    // 0值则使用默外部传入的精度
    fixedNumber = decimal;
  }
  if (value < 0) return `-${ratioValueFormat(val, fixedNumber, false, emptyValue, false, 3)}`;
  return ratioValueFormat(val, fixedNumber, false, emptyValue, false, 3);
};

/**
 * @description: 卡片位指标数值超过百万，需要增加单位M|B，不超过百万，则千分位正数展示
 * @param {number} value
 * @return {string}
 */
export const formatMoreThanMillion = (value: number): string => {
  const formatter = numeral(value);
  // 小于1000000，则用千分位展示，大于则展示bmk两位小数
  return ((value < 1000000 && value > 0 ) || (value > -1000000 && value < 0)) ? formatter.format('0,0') : formatter?.format('db_bmk00');
};

/**
 * @description: 自定义数据格式处理：百分比取正值
 * @param {number} value
 * @param {number} decimal
 * @return {string}
 */
export const formatAbsPercentValue = (value: number, decimal: number) => {
  // 转换为绝对值
  const absPercentage = Math.abs(value);
  return ratioValueFormat(absPercentage, decimal, true, '-', false, 3);
};

/**
 * 格式化数值，将数字转换为最多保留两位小数的形式
 * 四舍五入
 * * 整数可以展示为整数
 * * 1.2 展示为 1.2;  不展示为 1.20
 * * 2.8234 展示为 2.82
 * @param num - 需要格式化的数值，可以是字符串、数字或 null
 * @param decimal 小数位数
 * @returns 如果输入是有效数字，返回保留两位小数的数值；如果是无效数字，则返回原值
 */
export const roundingWithoutZero = <T extends string | number>(num: T, decimal = 2): T => {
  const parsedNum: number = parseFloat(num as string);
  if (isNaN(parsedNum)) return num;
  // 如果是整数，则返回整数字符串；否则返回保留两位小数的字符串
  const pow = 10 ** decimal;
  return (Math.round(parsedNum * pow) / pow)?.toString() as T;
};

/**
 * @description: 通用数值格式化
 * @param {ICommonValueFormatParams} params 
 * @return {string | number}
 */
export const commonValueFormat = (params: ICommonValueFormatParams): string | number => {
  const { value, type, decimal = 2, fillZero = true, emptyValue } = params || {};

  if (type === 'int') {
    return decimalFormat(value, 0, fillZero, emptyValue);
  }
  if (type === 'decimals') {
    return decimalFormat(value, decimal, fillZero, emptyValue);
  }
  if (type === 'percent') {
    return ratioValueFormat(value, decimal, fillZero, emptyValue, false, 3);
  }
  if (type === 'bigPercent') {
    return ratioValueFormat(value, decimal, fillZero, emptyValue, false, 4);
  }
  if (type === 'thousandsInt') {
    return thousandthFormat(value, 0, fillZero, emptyValue);
  }
  if (type === 'thousandsDecimals') {
    return thousandthFormat(value, decimal, fillZero, emptyValue);
  }
  if (type === 'smallDecimal') {
    return formatSmallValue({ value, type, decimal,emptyValue });
  }
  if (type === 'smallPercent') {
    return formatSmallPercent({value, decimal,isAxis: false, emptyValue});
  }
  if (type === 'axisSmallPercent') {
    return formatSmallPercent({value, decimal,isAxis: true, emptyValue});
  }
  if(type === 'bmk') {
    return financeFormat(value, decimal, fillZero, emptyValue);
  }
  if(type === 'roundingWithoutZero') {
    return roundingWithoutZero(value, decimal);
  }
  // 默认返回原值
  return value;
};